import { setAppContext, appContext } from './DataBindingAppContext'
import { isEqual } from 'lodash'
import { ROUTER_DATASET, WIDGET_DATASET } from '@wix/wix-data-client-common'
import { createSenderOfViewerStatusForEditor } from '../helpers/createSenderOfViewerStatusForEditor'
import DataProvider from '../data/DataProvider'
import {
  convertFromCustomFormat,
  convertToCustomFormat,
} from '@wix/cloud-elementory-protocol'
import { PRIMARY } from '../data/sequenceType'
import completeControllerConfigs from '../dataset-controller/completeControllerConfigs'
import { Deferred } from '../helpers'
import { createRecordStoreService } from '../record-store'
import createControllerFactory from '../dataset-controller/controllerFactory'
import { createDependencyManager } from '../dataset-controller/dependencyManager'
import { createSeoManager } from '../seo/seoManager'
import AppState from './AppState'
import {
  DataBindingLogger,
  Trace,
  Breadcrumb,
  createErrorReporting,
  createBreadcrumbReporting,
  createVerboseReporting,
} from '../logger'
import datasetEntity from '../dataset/dataset-entity'
import { createLastActionManager } from '../helpers/LastActionManager'

export default class DataBinding {
  constructor({
    platform,
    dataFetcher,
    warmupCache,
    staticCache,
    features,
    listenersByEvent,
    logger,
    i18n,
    global,
    //TODO: add to warmupCache, and distinguish there which data should be saved to warmup store.
    // for now routerData schemas are also saved to the warmup data which is not oprimal
  }) {
    const dataBindingLogger = new DataBindingLogger(logger, global)

    setAppContext({
      platform,
      features,
      dataFetcher,
      i18n,
      appState: new AppState(),
      logger: dataBindingLogger,
      errorReporting: createErrorReporting(dataBindingLogger),
      breadcrumbReporting: createBreadcrumbReporting(dataBindingLogger),
      verboseReporting: createVerboseReporting(dataBindingLogger),
      datasetStore: {},
      lastAction: createLastActionManager(),
    })

    this._listenersByEvent = listenersByEvent
    this._dataProvider = new DataProvider()
    this._warmupCache = warmupCache
    this._staticCache = staticCache
    this._features = features
    this._logger = dataBindingLogger

    //TODO: invert
    this._recordStoreCache = {}
  }

  initializeDatasets({
    //TODO: temp interface
    datasetConfigs,
    firePlatformEvent,
  }) {
    try {
      return this._logger.log(
        new Trace('databinding/createControllers', () =>
          this._initializeDatasets({ datasetConfigs, firePlatformEvent }),
        ),
      )
    } catch (e) {
      this._logger.logError(e, 'Datasets initialisation failed')
      return []
    }
  }

  _initializeDatasets({
    //TODO: temp interface
    datasetConfigs: datasets,
    firePlatformEvent,
  }) {
    const {
      platform: {
        postMessage,
        settings: {
          mode: { name: modeName, csr, ssr, verbose },
          env: { livePreview, live },
        },
        seo,
        location,
      },
    } = appContext

    const datasetStore = datasets.reduce((datasetStore, dataset) => {
      const updatedDataset = datasetEntity.initCurrentPage(
        dataset,
        location.queryParams,
      )
      datasetStore[datasetEntity.getId(dataset)] = updatedDataset
      return datasetStore
    }, appContext.datasetStore) //TODO: extract to separate abstraction datasetStore with set and merge methods
    // Fix handles widget inside dynamic repeater case - createControllers with new widget dataset instances was being called
    // immediately after we set data to repeater, which overrided the previous datasetStore
    // WD-8643
    setAppContext({ datasetStore })

    const datasetConfigs = completeControllerConfigs(
      datasets.map(dataset => datasetStore[datasetEntity.getId(dataset)]),
    )

    const updatedDatasetIds = this._updateDatasetConfigsState(datasetConfigs)
    const warmupDataIsEnabled = this._features.warmupData

    const fetchingAllDatasetsData = []
    const fetchingAllDatasetsDataForWarmupCache = []
    const renderingControllers = []
    const {
      resolve: renderDeferredControllers,
      promise: renderingRegularControllers,
    } = new Deferred()

    /* This solution guarantees:
    - Static cache may have couple of collections, if router collection has referenced collections.
      But if we have regular datasets connected to those collections, we want refetch them in order to not loose includes
    - If we have router dataset schema loaded, we don't want to refetch it
    - If we don't have router dataset schema loaded, we want to fetch it
    - DataProvider receives all collection ids because it would try to load any schema not present in static cache.
      That's why we remove referenced schemas we want to refetch from static cache.
    */
    const collectionCandidatesToLoad = getUniqueCollectionIdCandidatesToLoad(datasetConfigs)
    const staticCacheSchemas = this._staticCache.getSchemas()
    const filteredStaticCacheSchemas = staticCacheSchemas ? collectionCandidatesToLoad.regularDatasetCollectionIds.reduce((schemas, collectionId) => {
      if (schemas[collectionId]) {
        delete schemas[collectionId]
      }
      return schemas
    }, staticCacheSchemas) : {}

    const preloadedSchemas = {
      ...((warmupDataIsEnabled && csr && this._warmupCache.getSchemas()) || {}),
      ...filteredStaticCacheSchemas,
      ...getWidgetEmptySchemas(datasets),
    }
    this._dataProvider.setSchemas(preloadedSchemas)

    const schemasLoading = this._logger.log(
      new Trace('databinding/loadSchemas', () =>
        this._dataProvider
          .loadSchemas(collectionCandidatesToLoad.allDatasetCollectionIds)
          .then(
            () =>
              warmupDataIsEnabled &&
              ssr &&
              this._warmupCache.setSchemas(this._dataProvider.getSchemas()),
          ),
      ),
    )

    // TODO: Remove after refactoring datasetConfigs
    this._dataProvider.setDatasetConfigs(
      datasetConfigs.map(
        ({
          config: {
            dataset: { collectionName: collectionId },
          },
          compId: datasetId,
          type,
        }) => ({
          collectionId,
          datasetId,
          type,
        }),
      ),
    )

    const warmupStore =
      csr && warmupDataIsEnabled && this._warmupCache.getDataStore()
    if (warmupStore) {
      this._dataProvider.setStore(convertFromCache(warmupStore))
    }

    const staticStore = this._staticCache.getDataStore()
    if (staticStore) {
      this._dataProvider.setStaticStore(convertFromCache(staticStore))
    }

    this._dataProvider.createInitialDataRequest(
      this._getInitialDataRequestConfigs(datasetConfigs, updatedDatasetIds),
    )

    const connectionsGraph = datasetConfigs.reduce(
      (g, { compId, connections }) => {
        g[compId] = connections.map(({ compId }) => compId)
        return g
      },
      {},
    )

    const dependencyManager = createDependencyManager({
      datasetConfigs,
    })

    const containsFieldsInPattern = dynamicUrl => !!dynamicUrl?.match(/\{.+\}/)
    const isDynamicItemPage = datasetConfigs.some(
      ({ type, dynamicPageData }) =>
        type === ROUTER_DATASET &&
        containsFieldsInPattern(dynamicPageData?.dynamicUrl),
    )

    const dynamicPageUsesSeoV2 =
      isDynamicItemPage &&
      datasetConfigs.some(
        ({ type, dynamicPageData }) =>
          type === ROUTER_DATASET && dynamicPageData?.seoV2,
      )

    const shouldCollectSeoData =
      live && isDynamicItemPage && dynamicPageUsesSeoV2

    const seoManager = createSeoManager({
      seo,
      pageUrl: location.pageUrl,
    })

    const statusForEditor = createSenderOfViewerStatusForEditor({
      global: self,
      postMessage,
      enabled: verbose,
    })

    const controllers = datasetConfigs.map(datasetConfig => {
      const {
        type,
        config,
        connections,
        compId: datasetId,
        livePreviewOptions: {
          shouldFetchData: dataIsInvalidated,
          compsIdsToReset: updatedCompIds = [],
        } = {},
        dynamicPageData,
      } = datasetConfig
      const { datasetIsRouter, datasetIsDeferred } = config.datasetStaticConfig
      this._logger.log(
        new Breadcrumb({
          category: 'createControllers',
          message: 'warmup data contents',
          data: {
            datasetId,
            datasetType: type,
            mode: modeName,
            warmupData: Boolean(warmupStore),
          },
        }),
      )

      const recordStoreService = createRecordStoreService({
        primaryDatasetId: datasetId,
        recordStoreCache: this._recordStoreCache,
        refreshStoreCache: dataIsInvalidated,
        dataProvider: this._dataProvider,
        controllerConfig: config,
        statusForEditor,
      })

      const { promise: fetchingDatasetData, resolve: markDatasetDataFetched } =
        new Deferred()
      if (!datasetIsDeferred) {
        fetchingAllDatasetsData.push(fetchingDatasetData)
        if (!datasetIsRouter) {
          fetchingAllDatasetsDataForWarmupCache.push(fetchingDatasetData)
        }
      }
      const {
        promise: renderingController,
        resolve: markControllerAsRendered,
      } = new Deferred()
      renderingControllers.push(renderingController)

      const warmupFilterInitialData =
        csr &&
        warmupDataIsEnabled &&
        this._warmupCache.getUserFilterInitialData(datasetId)

      if (warmupFilterInitialData) {
        this._dataProvider.setUserFilterInitialData(
          datasetId,
          warmupFilterInitialData,
        )
      }

      const controllerFactory = createControllerFactory(this._logger, {
        shouldCollectSeoData,
        seoManager,
        dependencyManager,
        controllerConfig: config,
        datasetType: type,
        connections,
        connectionsGraph,
        recordStoreService,
        dataProvider: this._dataProvider,
        firePlatformEvent: firePlatformEvent(datasetId),
        dynamicPagesData:
          datasetIsRouter && dynamicPageData
            ? {
              dynamicPageData,
              items: this._staticCache.getItems(),
              datasetConfig: config,
            }
            : undefined,
        datasetId,
        schemasLoading,
        listenersByEvent: this._listenersByEvent,
        updatedCompIds,
        markControllerAsRendered,
        markDatasetDataFetched,
        renderingRegularControllers,
        modeIsLivePreview: livePreview,
        modeIsSSR: ssr,
        dataset: datasetConfig,
      })

      const dataset = controllerFactory.createRealDataset()
      dependencyManager.registerDataset({
        id: datasetId,
        api: dataset.api,
        config,
      })

      if (ssr && warmupDataIsEnabled) {
        dataset.userFilterInitialDataPromise.then(userFilterInitialData => {
          this._warmupCache.setUserFilterInitialData(
            datasetId,
            userFilterInitialData,
          )
        })
      }

      return dataset
    })

    if (
      ssr &&
      warmupDataIsEnabled &&
      fetchingAllDatasetsDataForWarmupCache.length
    ) {
      Promise.all(fetchingAllDatasetsDataForWarmupCache).then(() => {
        this._warmupCache.setDataStore(
          convertToCache(this._dataProvider.getStore()),
        )
      })
    }
    Promise.all(renderingControllers).then(renderDeferredControllers)

    const renderSeoTags = () =>
      Promise.all([...fetchingAllDatasetsData, schemasLoading]).then(
        seoManager.renderSeoTagsOnce,
      )

    return shouldCollectSeoData
      ? controllers.map(controller => ({
        ...controller,
        pageReady: (...args) =>
          controller.pageReady(...args).then(renderSeoTags),
      }))
      : controllers
  }

  _updateDatasetConfigsState(datasetConfigs) {
    const { appState } = appContext
    return datasetConfigs.reduce(
      (updatedDatasetIds, { compId: datasetId, config: { dataset } }) => {
        const datasetConfigState = appState.datasetConfigs.get(datasetId)
        if (datasetConfigState && !isEqual(datasetConfigState, dataset)) {
          updatedDatasetIds.push(datasetId)
        }
        appState.datasetConfigs.set(datasetId, dataset)

        return updatedDatasetIds
      },
      [],
    )
  }

  _getInitialDataRequestConfigs(datasetConfigs, updatedDatasetIds) {
    return datasetConfigs.reduce(
      (
        acc,
        {
          compId: datasetId,
          config: {
            datasetStaticConfig: { sequenceType },
          },
          livePreviewOptions: { shouldFetchData } = {},
        },
      ) =>
        sequenceType === PRIMARY
          ? [
            ...acc,
            {
              id: datasetId,
              refresh:
                shouldFetchData || updatedDatasetIds.includes(datasetId),
            },
          ]
          : acc,
      [],
    )
  }
}

const getUniqueCollectionIdCandidatesToLoad = (datasetConfigs) => {
  const [{ config: { dataset: { collectionName: routerDatasetCollectionId } } } = { config: { dataset: {} } }] = datasetConfigs.filter(
    ({ type }) => type === ROUTER_DATASET,
  )

  const uniqueCollectionIds = datasetConfigs.reduce(
    (
      uniqueIds,
      {
        type,
        config: {
          dataset: { collectionName },
        },
      },
    ) => {
      if (type !== WIDGET_DATASET && collectionName && collectionName !== routerDatasetCollectionId) {
        uniqueIds.add(collectionName)
      }
      return uniqueIds
    },
    new Set(),
  )

  const regularDatasetCollectionIds = [...uniqueCollectionIds]
  const allDatasetCollectionIds = [...uniqueCollectionIds]
  if (routerDatasetCollectionId) {
    allDatasetCollectionIds.push(routerDatasetCollectionId)
  }

  return { allDatasetCollectionIds, regularDatasetCollectionIds }
}

const getWidgetEmptySchemas = datasets =>
  datasets.reduce((schemas, dataset) => {
    if (datasetEntity.isWidget(dataset)) {
      schemas[datasetEntity.getCollectionId(dataset)] = {}
    }
    return schemas
  }, {})

const createConverter = convert => dataStore => {
  // TODO: change date format to ISO string and this conversion won't be needed
  if (dataStore) {
    return {
      ...dataStore,
      recordsByCollectionId: Object.entries(
        dataStore.recordsByCollectionId,
      ).reduce((acc, [collection, recordsById]) => {
        acc[collection] = convert(recordsById)
        return acc
      }, {}),
    }
  }
}
const convertToCache = createConverter(convertToCustomFormat)
const convertFromCache = createConverter(convertFromCustomFormat)
